'use client';
import { FC, PropsWithChildren, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import {
  Portal,
  Drawer,
  SubheaderControlCenterOrOrderHistory
} from 'components/pcc-components';
import { getSeoUrl } from 'utils/links';
import { useParams, usePathname } from 'next/navigation';
import { ROOT_PORTALS } from 'constants/targets';
import { returnFocusToNavItem } from '../../utils/common-utils';
import { useEsiteCtx } from 'services/esite/context';
import { getControlCenterLink, getOrderHistoryLink } from './utils';
import { HEADLESS_MY_ACCOUNT_LINKS } from 'constants/links';
import { useNavigationDrawers } from '../navigation-drawers-context';

export const ControlCenterOrOrderHistoryClient: FC<
  PropsWithChildren<{
    isMobile?: boolean;
    isControlCenterEnabled?: boolean;
    isControlCenterFeatureFlagEnabled?: boolean;
    isOrderHistoryFeatureFlagEnable?: boolean;
    isLoggedIn: boolean;
  }>
> = ({
  children,
  isMobile = false,
  isControlCenterEnabled = false,
  isControlCenterFeatureFlagEnabled = false,
  isLoggedIn = false,
  isOrderHistoryFeatureFlagEnable = false
}) => {
  const t = useTranslations();
  const params = useParams();
  const pathname = usePathname();
  const controlCenterOrOrderHistoryNavItemId =
    'controlCenterOrOrderHistoryNavItem';

  const {
    controlCenterOrOrderHistoryDrawer: {
      drawerProps,
      initiatorProps,
      isDrawerOpen
    }
  } = useNavigationDrawers();
  const drawerWasOpen = useRef(false);
  const { catalogId, langId, storeId } = useEsiteCtx();
  const navItemLabel = isControlCenterEnabled
    ? 'CONTROL_CENTER'
    : 'MA_ORDER_HISTORY';
  const controlCenterURL = isControlCenterFeatureFlagEnabled
    ? getSeoUrl(params, HEADLESS_MY_ACCOUNT_LINKS.CONTROL_CENTER)
    : getControlCenterLink(storeId, langId, catalogId);
  const orderHistoryURL = isOrderHistoryFeatureFlagEnable
    ? getSeoUrl(params, HEADLESS_MY_ACCOUNT_LINKS.ORDER_HISTORY)
    : getOrderHistoryLink(storeId, langId, catalogId);
  const navItemHref = isControlCenterEnabled
    ? controlCenterURL
    : orderHistoryURL;

  const navItemProps = {
    text: t(navItemLabel),
    href: !isLoggedIn ? undefined : navItemHref,
    ...(!isLoggedIn ? initiatorProps : {})
  };

  useEffect(() => {
    if (!isDrawerOpen && drawerWasOpen.current) {
      returnFocusToNavItem(`#${controlCenterOrOrderHistoryNavItemId}`);
      drawerWasOpen.current = false;
    } else if (isDrawerOpen && !drawerWasOpen.current) {
      drawerWasOpen.current = true;
    }
  }, [isDrawerOpen]);
  const isNavItemActive = navItemHref === pathname;

  return (
    <>
      <SubheaderControlCenterOrOrderHistory
        id="controlCenterOrOrderHistoryNavItem"
        isControlCenterEnabled={isControlCenterEnabled}
        navItemProps={navItemProps}
        isCurrent={isDrawerOpen || isNavItemActive}
        displayMobileNavLink={isMobile}
      />
      <Portal id={ROOT_PORTALS}>
        <Drawer
          data-testid="controlCenterOrOrderHistoryDrawer"
          {...drawerProps}
        >
          {children}
        </Drawer>
      </Portal>
    </>
  );
};
